import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCwIcon } from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  fetchIbgeSincronizacaoStatus,
  ibgeSyncKeys,
  triggerIbgeSincronizacao,
} from '@/services/ibgeSyncService';
import {
  fetchSincronizacaoStatus,
  pgaSigsifSyncKeys,
  triggerSincronizacao,
} from '@/services/pgaSigsifSyncService';
import { parseApiError } from '@/utils/apiError';
import { formatDateTime } from '@/utils/date';
import type { TIbgeRecurso } from '@/types/ibgeSync';
import type { TPgaSigsifRecurso } from '@/types/pgaSigsifSync';

export const Route = createFileRoute('/_authenticated/admin/sincronizacao-pga/')({
  beforeLoad: ({ context }) => {
    if (context.auth.loading) {
      return;
    }

    if (context.auth.user?.role !== 'super_admin') {
      toast.error('Você não tem permissão para acessar esta tela.');
      return redirect({ to: '/dashboard' });
    }
  },
  component: SincronizacaoDados,
});

const PGA_RECURSO_LABELS: Record<TPgaSigsifRecurso, string> = {
  especies: 'Espécies',
  diagnosticos: 'Diagnósticos',
  'destino-condenacoes': 'Destinos de condenação',
  'partes-afetadas': 'Partes afetadas',
  paises: 'Países',
  produtos: 'Produtos',
};

const PGA_RECURSOS_ORDENADOS: TPgaSigsifRecurso[] = [
  'produtos',
  'especies',
  'diagnosticos',
  'destino-condenacoes',
  'partes-afetadas',
  'paises',
];

const IBGE_RECURSO_LABELS: Record<TIbgeRecurso, string> = {
  regioes: 'Regiões (UFs)',
  municipios: 'Municípios',
};

const IBGE_RECURSOS_ORDENADOS: TIbgeRecurso[] = ['regioes', 'municipios'];

type TSyncStatusLike = {
  status: string | null;
  finalizado_em?: string | null;
  iniciado_em?: string | null;
  registros_encontrados?: number | null;
  registros_salvos?: number | null;
  registros_ignorados?: number | null;
  mensagem_erro?: string | null;
};

function statusLabel(item: TSyncStatusLike): { text: string; className: string } {
  if (item.status === 'executando') return { text: 'Sincronizando…', className: 'text-primary' };
  if (item.status === 'erro') return { text: 'Erro', className: 'text-error' };
  if (item.status === 'sucesso') return { text: 'Sincronizado', className: 'text-success' };
  return { text: 'Nunca sincronizado', className: 'text-on-surface-variant' };
}

function SyncCard({
  title,
  item,
  onSync,
  isPending,
}: {
  title: string;
  item: TSyncStatusLike | undefined;
  onSync: () => void;
  isPending: boolean;
}) {
  const sl = item ? statusLabel(item) : statusLabel({ status: null });
  const executando = item?.status === 'executando';

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-on-surface">{title}</h3>
        <span className={`text-sm font-medium ${sl.className}`}>{sl.text}</span>
      </div>

      <div className="flex flex-col gap-1 text-sm text-on-surface-variant">
        <div className="flex justify-between">
          <span>Última execução:</span>
          <span className="text-on-surface">{formatDateTime(item?.finalizado_em ?? item?.iniciado_em)}</span>
        </div>
        {item?.status === 'sucesso' && (
          <>
            <div className="flex justify-between">
              <span>Encontrados:</span>
              <span className="text-on-surface">{item.registros_encontrados ?? '-'}</span>
            </div>
            <div className="flex justify-between">
              <span>Salvos:</span>
              <span className="text-on-surface">{item.registros_salvos ?? '-'}</span>
            </div>
            <div className="flex justify-between">
              <span>Ignorados:</span>
              <span className="text-on-surface">{item.registros_ignorados ?? '-'}</span>
            </div>
          </>
        )}
        {item?.status === 'erro' && item.mensagem_erro && (
          <p className="mt-1 text-error">{item.mensagem_erro}</p>
        )}
      </div>

      <Button
        variant="outline"
        onClick={onSync}
        disabled={executando || isPending}
        className="mt-auto"
      >
        {isPending || executando ? (
          <>
            <Spinner data-icon="inline-start" />
            Sincronizando…
          </>
        ) : (
          <>
            <RefreshCwIcon data-icon="inline-start" />
            Sincronizar agora
          </>
        )}
      </Button>
    </div>
  );
}

function SincronizacaoDados() {
  const queryClient = useQueryClient();

  const pgaQuery = useQuery({
    queryKey: pgaSigsifSyncKeys.status(),
    queryFn: fetchSincronizacaoStatus,
    refetchInterval: (query) => {
      const items = query.state.data ?? [];
      return items.some((item) => item.status === 'executando') ? 3000 : false;
    },
  });

  const ibgeQuery = useQuery({
    queryKey: ibgeSyncKeys.status(),
    queryFn: fetchIbgeSincronizacaoStatus,
    refetchInterval: (query) => {
      const items = query.state.data ?? [];
      return items.some((item) => item.status === 'executando') ? 3000 : false;
    },
  });

  const pgaTrigger = useMutation({
    mutationFn: triggerSincronizacao,
    onSuccess: (result) => {
      toast.success(`${PGA_RECURSO_LABELS[result.recurso]}: sincronização iniciada.`);
      void queryClient.invalidateQueries({ queryKey: pgaSigsifSyncKeys.status() });
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Erro ao iniciar sincronização PGA.'));
    },
  });

  const ibgeTrigger = useMutation({
    mutationFn: triggerIbgeSincronizacao,
    onSuccess: (result) => {
      toast.success(`${IBGE_RECURSO_LABELS[result.recurso]}: sincronização iniciada.`);
      void queryClient.invalidateQueries({ queryKey: ibgeSyncKeys.status() });
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Erro ao iniciar sincronização IBGE.'));
    },
  });

  const pgaPorRecurso = new Map((pgaQuery.data ?? []).map((item) => [item.recurso, item]));
  const ibgePorRecurso = new Map((ibgeQuery.data ?? []).map((item) => [item.recurso, item]));
  const isLoading = pgaQuery.isLoading || ibgeQuery.isLoading;
  const isError = pgaQuery.isError || ibgeQuery.isError;
  const error = pgaQuery.error ?? ibgeQuery.error;

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Sincronização de dados de referência</h1>
        <p className="text-on-surface-variant">
          Sincronize cadastros externos (PGA SIGSIF e IBGE) usados nas telas do sistema. Há também
          rotina agendada — use aqui só para forçar atualização imediata.
        </p>
      </div>

      {isError && (
        <div
          role="alert"
          className="rounded-lg bg-error-container p-4 text-on-error-container"
        >
          {parseApiError(error, 'Erro ao carregar status de sincronização.')}
        </div>
      )}

      {isLoading ? (
        <LoadingSpinner text="Carregando status…" />
      ) : (
        <>
          <section className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-on-surface">IBGE — localidades</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {IBGE_RECURSOS_ORDENADOS.map((recurso) => {
                const item = ibgePorRecurso.get(recurso);
                return (
                  <SyncCard
                    key={recurso}
                    title={IBGE_RECURSO_LABELS[recurso]}
                    item={item}
                    isPending={ibgeTrigger.isPending && ibgeTrigger.variables === recurso}
                    onSync={() => void ibgeTrigger.mutateAsync(recurso)}
                  />
                );
              })}
            </div>
          </section>

          <section className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-on-surface">PGA SIGSIF</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {PGA_RECURSOS_ORDENADOS.map((recurso) => {
                const item = pgaPorRecurso.get(recurso);
                return (
                  <SyncCard
                    key={recurso}
                    title={PGA_RECURSO_LABELS[recurso]}
                    item={item}
                    isPending={pgaTrigger.isPending && pgaTrigger.variables === recurso}
                    onSync={() => void pgaTrigger.mutateAsync(recurso)}
                  />
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
